import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { ChevronDownIcon, ChevronUpIcon, Tool02Icon } from '@untitledui-icons/react/line'
import { cn } from '@/lib/utils'

/**
 * Collapsible panel that displays Jinja2 template reference documentation
 * for simulation scenario fields that support dynamic template variables.
 */
export function TemplateReferencePanel() {
  const { t } = useTranslation()
  const [isExpanded, setIsExpanded] = useState(false)

  return (
    <div className="border-border-secondary rounded-lg border bg-gray-50 dark:bg-gray-900">
      <button
        type="button"
        onClick={() => setIsExpanded(!isExpanded)}
        className={cn(
          'flex w-full items-center justify-between px-4 py-3',
          'text-sm font-medium text-gray-700 dark:text-gray-300',
          'hover:bg-gray-100 dark:hover:bg-gray-800',
          'rounded-lg transition-colors',
          isExpanded && 'rounded-b-none'
        )}
      >
        <div className="flex items-center gap-2">
          <Tool02Icon className="h-4 w-4 text-brand-500" />
          <span>{t('pages.simulations.scenarios.template_reference.title')}</span>
        </div>
        {isExpanded ? (
          <ChevronUpIcon className="h-4 w-4" />
        ) : (
          <ChevronDownIcon className="h-4 w-4" />
        )}
      </button>

      {isExpanded && (
        <div className="border-t border-gray-200 px-4 py-4 dark:border-gray-700">
          {/* Description */}
          <p className="text-fg-secondary mb-4 text-sm">
            {t('pages.simulations.scenarios.template_reference.description')}
          </p>

          {/* Supported Fields */}
          <div className="mb-4">
            <h4 className="text-fg-primary mb-2 text-sm font-medium">
              {t('pages.simulations.scenarios.template_reference.supported_fields')}
            </h4>
            <ul className="text-fg-secondary list-inside list-disc space-y-1 text-sm">
              <li>{t('pages.simulations.scenarios.fields.scenario_prompt')}</li>
              <li>{t('pages.simulations.scenarios.fields.pre_call_prompt_variables')}</li>
              <li>{t('pages.simulations.scenarios.fields.pre_call_prepend_messages')}</li>
              <li>{t('pages.simulations.scenarios.fields.mock_responses')}</li>
              <li>{t('pages.simulations.scenarios.fields.post_call_mock_responses')}</li>
            </ul>
          </div>

          {/* Available Variables */}
          <div className="mb-4">
            <h4 className="text-fg-primary mb-2 text-sm font-medium">
              {t('pages.simulations.scenarios.template_reference.available_variables')}
            </h4>
            <div className="overflow-x-auto">
              <table className="text-fg-secondary w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-200 dark:border-gray-700">
                    <th className="text-fg-primary py-2 pr-4 text-left font-medium">
                      {t('pages.simulations.scenarios.template_reference.variable')}
                    </th>
                    <th className="text-fg-primary py-2 pr-4 text-left font-medium">
                      {t('pages.simulations.scenarios.template_reference.type')}
                    </th>
                    <th className="text-fg-primary py-2 text-left font-medium">
                      {t('pages.simulations.scenarios.template_reference.description_header')}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b border-gray-100 dark:border-gray-800">
                    <td className="py-2 pr-4">
                      <code className="rounded bg-gray-200 px-1.5 py-0.5 text-xs dark:bg-gray-700">
                        today
                      </code>
                    </td>
                    <td className="py-2 pr-4">date</td>
                    <td className="py-2">
                      {t('pages.simulations.scenarios.template_reference.var_today')}
                    </td>
                  </tr>
                  <tr className="border-b border-gray-100 dark:border-gray-800">
                    <td className="py-2 pr-4">
                      <code className="rounded bg-gray-200 px-1.5 py-0.5 text-xs dark:bg-gray-700">
                        tomorrow
                      </code>
                    </td>
                    <td className="py-2 pr-4">date</td>
                    <td className="py-2">
                      {t('pages.simulations.scenarios.template_reference.var_tomorrow')}
                    </td>
                  </tr>
                  <tr className="border-b border-gray-100 dark:border-gray-800">
                    <td className="py-2 pr-4">
                      <code className="rounded bg-gray-200 px-1.5 py-0.5 text-xs dark:bg-gray-700">
                        yesterday
                      </code>
                    </td>
                    <td className="py-2 pr-4">date</td>
                    <td className="py-2">
                      {t('pages.simulations.scenarios.template_reference.var_yesterday')}
                    </td>
                  </tr>
                  <tr className="border-b border-gray-100 dark:border-gray-800">
                    <td className="py-2 pr-4">
                      <code className="rounded bg-gray-200 px-1.5 py-0.5 text-xs dark:bg-gray-700">
                        now
                      </code>
                    </td>
                    <td className="py-2 pr-4">datetime</td>
                    <td className="py-2">
                      {t('pages.simulations.scenarios.template_reference.var_now')}
                    </td>
                  </tr>
                  <tr className="border-b border-gray-100 dark:border-gray-800">
                    <td className="py-2 pr-4">
                      <code className="rounded bg-gray-200 px-1.5 py-0.5 text-xs dark:bg-gray-700">
                        timedelta(...)
                      </code>
                    </td>
                    <td className="py-2 pr-4">function</td>
                    <td className="py-2">
                      {t('pages.simulations.scenarios.template_reference.var_timedelta')}
                    </td>
                  </tr>
                  <tr>
                    <td className="py-2 pr-4">
                      <code className="rounded bg-gray-200 px-1.5 py-0.5 text-xs dark:bg-gray-700">
                        arguments.*
                      </code>
                    </td>
                    <td className="py-2 pr-4">dict</td>
                    <td className="py-2">
                      {t('pages.simulations.scenarios.template_reference.var_arguments')}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          {/* Date Syntax Examples */}
          <div className="mb-4">
            <h4 className="text-fg-primary mb-2 text-sm font-medium">
              {t('pages.simulations.scenarios.template_reference.date_examples')}
            </h4>
            <div className="space-y-2">
              <div className="rounded-md bg-gray-200 p-3 dark:bg-gray-800">
                <code className="text-fg-primary block text-xs">
                  {'{{ today.strftime(\'%Y-%m-%d\') }}'}
                </code>
                <span className="text-fg-tertiary mt-1 block text-xs">
                  → "2026-02-05"
                </span>
              </div>
              <div className="rounded-md bg-gray-200 p-3 dark:bg-gray-800">
                <code className="text-fg-primary block text-xs">
                  {'{{ tomorrow.strftime(\'%Y-%m-%d\') }}'}
                </code>
                <span className="text-fg-tertiary mt-1 block text-xs">
                  → "2026-02-06"
                </span>
              </div>
              <div className="rounded-md bg-gray-200 p-3 dark:bg-gray-800">
                <code className="text-fg-primary block text-xs">
                  {'{{ (today + timedelta(days=7)).strftime(\'%Y-%m-%d\') }}'}
                </code>
                <span className="text-fg-tertiary mt-1 block text-xs">
                  → "2026-02-12" ({t('pages.simulations.scenarios.template_reference.days_from_now', { count: 7 })})
                </span>
              </div>
              <div className="rounded-md bg-gray-200 p-3 dark:bg-gray-800">
                <code className="text-fg-primary block text-xs">
                  {'{{ today.strftime(\'%Y-%m-%d\') }}T19:00:00+01:00'}
                </code>
                <span className="text-fg-tertiary mt-1 block text-xs">
                  → "2026-02-05T19:00:00+01:00"
                </span>
              </div>
            </div>
          </div>

          {/* Mock Response Examples */}
          <div>
            <h4 className="text-fg-primary mb-2 text-sm font-medium">
              {t('pages.simulations.scenarios.template_reference.mock_examples')}
            </h4>
            <p className="text-fg-tertiary mb-2 text-xs">
              {t('pages.simulations.scenarios.template_reference.mock_examples_description')}
            </p>
            <div className="space-y-2">
              <div className="rounded-md bg-gray-200 p-3 dark:bg-gray-800">
                <code className="text-fg-primary block text-xs">
                  {'{{ arguments.party_size }}'}
                </code>
                <span className="text-fg-tertiary mt-1 block text-xs">
                  → "4" ({t('pages.simulations.scenarios.template_reference.value_from_tool')})
                </span>
              </div>
              <div className="rounded-md bg-gray-200 p-3 dark:bg-gray-800">
                <code className="text-fg-primary block text-xs">
                  {'{{ arguments.date }}'}
                </code>
                <span className="text-fg-tertiary mt-1 block text-xs">
                  → "2026-02-05" ({t('pages.simulations.scenarios.template_reference.value_from_tool')})
                </span>
              </div>
              <div className="rounded-md bg-gray-200 p-3 dark:bg-gray-800">
                <code className="text-fg-primary block text-xs">
                  {'Booking confirmed for {{ arguments.party_size }} guests on {{ arguments.date }}'}
                </code>
                <span className="text-fg-tertiary mt-1 block text-xs">
                  → "Booking confirmed for 4 guests on 2026-02-05"
                </span>
              </div>
            </div>
          </div>

          {/* Tip */}
          <div className="mt-4 rounded-md border border-brand-200 bg-brand-50 p-3 dark:border-brand-800 dark:bg-brand-950">
            <p className="text-brand-800 text-xs dark:text-brand-200">
              <strong>{t('pages.simulations.scenarios.template_reference.tip_label')}:</strong>{' '}
              {t('pages.simulations.scenarios.template_reference.tip_content')}
            </p>
          </div>
        </div>
      )}
    </div>
  )
}

export default TemplateReferencePanel
